import { type CanonicalEnvironment , isProductionEnvironment } from '../environment';
import { getCommitSha, getReactNativeVersion } from './getCommitSha';
import { getCachedTareData } from '../native-modules/tare/TareDataCache';

import {
    AvroOptional,
    type RNDevicePropertiesV4,
    RNAppsV4,
    BuildVariant,
    Environment,
} from '@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNDevicePropertiesV4';
import { Platform } from 'react-native';

/**
 * Narrow type that only includes the fully qualified variant format.
 * Although RNDevicePropertiesV4 is shared between mobPageLoadInfo and rnHttpError, there generated type is different.
 * We have to use this narrow type to avoid type errors.
 */
type NarrowRNDevicePropertiesV4 = Omit<RNDevicePropertiesV4, 'variant' | 'env'> & {
    variant?: null | {
        'com.indeed.signals.schema.reactnative.BuildVariant': BuildVariant;
    };
    env?: null | {
        'com.indeed.signals.schema.reactnative.Environment': Environment;
    };
};

/**
 * the version of react native that is running.
 *
 * @returns a semver string representation of the version number
 */
function getRNLibraryVersion(): string {
    return Platform.select({
        web: getReactNativeVersion,
        default: () => {
            const { major, minor, patch } = Platform.constants.reactNativeVersion;
            return `${major}.${minor}.${patch}`;
        },
    })();
}

/**
 * The version of the native app that is running.
 * @returns the app version from the TARE bridge cache, or '0.0.0' to indicate that the version is not available
 */
function getNativeAppVersion(): string {
    const tareData = getCachedTareData();
    return tareData?.appModel?.appVersion ?? '0.0.0';
}

/**
 * The version of the react native bundle that is running.
 *
 * Different from the native app version because it takes into account over-the-air updates.
 *
 * @returns a string representing the version of the app
 */
export function getRNAppBundleVersion(): string {
    return getCommitSha();
}

/**
 * Indicate whether we are running on the Job Seeker or Employer app.
 * @returns hardcoded to jsam (job seeker) since it is currently the only client
 */
function getAppName(): RNAppsV4 {
    return RNAppsV4.jsma;
}

/**
 * The version of the operating system in use
 * @returns a string representation of the version (on Android it is a number)
 */
function getOSVersion(): string {
    return `${Platform.Version}`;
}

/**
 * Safely cast a string to BuildVariant enum
 * @param buildConfig the build config string from TARE
 * @returns BuildVariant enum value or undefined if invalid
 */
function castToBuildVariant(
    buildConfig: string | undefined,
): { 'com.indeed.signals.schema.reactnative.BuildVariant': BuildVariant } | null {
    if (!buildConfig) {
        return null;
    }

    const lowerConfig = buildConfig.toLowerCase();
    switch (lowerConfig) {
        case 'debug':
            return {
                'com.indeed.signals.schema.reactnative.BuildVariant': BuildVariant.debug,
            };
        case 'release':
            return {
                'com.indeed.signals.schema.reactnative.BuildVariant': BuildVariant.release,
            };
        default:
            return null;
    }
}

/**
 * Safely cast a CanonicalEnvironment to Environment enum
 * @param buildEnvironment the canonical build environment from TARE
 * @returns Environment enum value or null if undefined
 */
function castToEnvironment(
    buildEnvironment: CanonicalEnvironment | undefined,
): { 'com.indeed.signals.schema.reactnative.Environment': Environment } | null {
    if (!buildEnvironment) {
        return null;
    }

    return {
        'com.indeed.signals.schema.reactnative.Environment': isProductionEnvironment(
            buildEnvironment,
        )
            ? Environment.prod
            : Environment.qa,
    };
}

/**
 * Information that we have access to in react native about the current device.
 *
 * The information that we have access to for android and ios are different so
 * this method provides a union type based on the platform that is running.
 *
 * @returns either android or ios device properties.
 */
export function getDeviceProperties(): NarrowRNDevicePropertiesV4 {
    const tareData = getCachedTareData();
    const commonProperties: {
        osVersion: string;
        appName: RNAppsV4;
        nativeAppVersion: string;
        rnLibraryVersion: string;
        rnAppBundleVersion: string;
        variant: { 'com.indeed.signals.schema.reactnative.BuildVariant': BuildVariant } | null;
        env: { 'com.indeed.signals.schema.reactnative.Environment': Environment } | null;
    } = {
        osVersion: getOSVersion(),
        appName: getAppName(),
        nativeAppVersion: getNativeAppVersion(),
        rnLibraryVersion: getRNLibraryVersion(),
        rnAppBundleVersion: getRNAppBundleVersion(),
        variant: castToBuildVariant(tareData?.appModel?.buildConfig),
        env: castToEnvironment(tareData?.appModel?.buildEnvironment),
    };

    if (Platform.OS === 'android') {
        return {
            ...commonProperties,
            platform: Platform.OS,
            manufacturer: AvroOptional.string(Platform.constants.Manufacturer),
            model: AvroOptional.string(Platform.constants.Model),
            brand: AvroOptional.string(Platform.constants.Brand),
            isIpad: false,
        };
    }

    // must be an if statement to determine the iOS specific types
    if (Platform.OS === 'ios') {
        return {
            ...commonProperties,
            platform: Platform.OS,
            systemName: AvroOptional.string(Platform.constants.systemName),
            isIpad: Platform.isPad,
        };
    }

    if (Platform.OS === 'web') {
        return {
            ...commonProperties,
            // TODO need to evolve the Signals schema to add a `web` enum value
            platform: 'android',
            isIpad: false,
        };
    }

    throw new Error(`Unexpected platform OS: ${Platform.OS}`);
}
